import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faPlay } from "@fortawesome/free-solid-svg-icons";
import ListenButton from "../LessonAudio/ListenButton";
import PaneResizer, { clampSize } from "./PaneResizer.js";
import { useUserAuth } from "../../context/UserContext.js";
import { getFreshIdToken } from "../../utils/authToken.js";
import { requestMyProject, saveMyProject } from "../../services/userService.js";
import {
    ACCENT_COLORS,
    DEFAULT_BRAND,
    buildStarterAppHtml,
    buildStarterAppLabels,
} from "../../starterTemplate/starterAppHtml.js";
import "./PromptLabLayout.css";
import "./CanvasLayout.css";
import "./TemplateStudioLayout.css";

const EMOJI_CHOICES = ["🚀", "🌮", "💪", "🧾", "🎓", "🛠️", "🌱", "❤️"];

/**
 * Module 1 "template" layout — the day-one win (lesson CD108):
 *
 *   +------------+‖--------------------+‖-----------------------------+
 *   |  Lesson    |‖   Make it yours    |‖   Your app                  |
 *   |  markdown  |‖   (the brand form) |‖   (live preview, iframe)    |
 *   +------------+‖--------------------+‖-----------------------------+
 *
 * Three columns, like the app-idea lesson (PromptLabLayout's `captureProject`
 * shape): the middle column is always where the student works, and the result of
 * that work sits to its right. The preview used to hang UNDER the form in a
 * shared right column, which buried the whole point of the lesson — the app —
 * below the fold on short screens.
 *
 * Same split-pane bones as the other layouts: two vertical `.pl-resizer`
 * dividers, each of which IS the border between two panes and also moves with
 * the arrow keys (see PaneResizer).
 *
 * The point of the lesson is emotional, not technical: about ten minutes in, the
 * student sees an app carrying their own name and colours actually running. It
 * is explicitly cosmetic — the real construction is Module 4. Completion is a
 * self-check (name it, then run it), mirroring how ConceptCanvas grades itself.
 *
 * The brand is saved to `users_projects`, the same doc CD107 wrote the idea to,
 * so it survives a reload and later feeds the real starter repo.
 */
const TemplateStudioLayout = ({
    definition,
    lessonTitle,
    onComplete,
    allPassed,
}) => {
    const { t } = useTranslation();
    const { user } = useUserAuth();

    const [leftWidth, setLeftWidth] = useState(420);
    const [middleWidth, setMiddleWidth] = useState(400);
    const containerRef = useRef(null);

    const [appName, setAppName] = useState(DEFAULT_BRAND.appName);
    const [tagline, setTagline] = useState(DEFAULT_BRAND.tagline);
    const [accentColor, setAccentColor] = useState(DEFAULT_BRAND.accentColor);
    const [emoji, setEmoji] = useState(DEFAULT_BRAND.emoji);

    // Read-only here — the idea is owned by CD107/V2101. We only read it to
    // personalize the preview copy; the brand save in handleRun must never write
    // it back (saveMyProject merges, so including it could clobber the idea).
    const [idea, setIdea] = useState(null);

    // The preview only reflects what they've explicitly run, so pressing "Run my
    // app" is a real moment rather than a field that quietly updates as they type.
    const [ranBrand, setRanBrand] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Initial split sizes, recomputed on window resize. Roughly thirds, like the
    // app-idea lesson, but the form is capped tighter than the lesson: it holds
    // four short fields and nothing else, so the width it doesn't need goes to
    // the preview, which is the thing the student came here to look at.
    useEffect(() => {
        const setInitial = () => {
            const el = containerRef.current;
            const totalW = el ? el.clientWidth : window.innerWidth;
            setLeftWidth(Math.min(520, Math.max(320, Math.floor(totalW * 0.32))));
            setMiddleWidth(Math.min(460, Math.max(300, Math.floor(totalW * 0.27))));
        };
        setInitial();
        window.addEventListener("resize", setInitial);
        return () => window.removeEventListener("resize", setInitial);
    }, []);

    // Prefill from whatever they already saved — the app name normally comes
    // from CD107, so most students arrive here with their idea already named.
    useEffect(() => {
        if (!user?.accessToken) return;
        let cancelled = false;

        (async () => {
            const token = await getFreshIdToken(user.accessToken);
            if (cancelled || !token) return;
            const project = await requestMyProject(token);
            if (cancelled || !project) return;
            if (project.appName) setAppName(project.appName);
            if (project.tagline) setTagline(project.tagline);
            if (project.accentColor) setAccentColor(project.accentColor);
            if (project.emoji) setEmoji(project.emoji);
            if (project.idea) setIdea(project.idea);

            // Returning to a finished lesson should show their app, not a blank
            // preview, so re-run it for them.
            if (project.appName) {
                setRanBrand({
                    appName: project.appName,
                    tagline: project.tagline || "",
                    accentColor: project.accentColor || DEFAULT_BRAND.accentColor,
                    emoji: project.emoji || DEFAULT_BRAND.emoji,
                });
            }
        })();

        return () => { cancelled = true; };
    }, [user?.accessToken]);

    // Both dividers drag the column to their LEFT; the preview always absorbs
    // the difference, so it can never be squeezed below ~280px.
    const dragColumn = (e, onX) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => onX(ev.clientX - rect.left, rect.width);
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // Each divider's limits for a given width, shared by dragging and the keyboard.
    const leftLimits = (width) => ({ min: 280, max: width - middleWidth - 12 - 280 });
    const middleLimits = (width) => ({ min: 260, max: width - leftWidth - 12 - 280 });
    const containerWidth = () => containerRef.current?.clientWidth || window.innerWidth;

    // Lesson ↔ form
    const startLeftResize = (e) =>
        dragColumn(e, (x, width) => {
            const { min, max } = leftLimits(width);
            setLeftWidth(clampSize(x, min, max));
        });

    // Form ↔ preview
    const startMiddleResize = (e) =>
        dragColumn(e, (x, width) => {
            const { min, max } = middleLimits(width);
            setMiddleWidth(clampSize(x - leftWidth - 6, min, max));
        });

    // Localized chrome for the rendered app. Rebuilt only when the language
    // changes so the iframe document isn't regenerated on every keystroke.
    const previewLabels = useMemo(() => buildStarterAppLabels(t), [t]);

    const previewHtml = useMemo(
        () => (ranBrand ? buildStarterAppHtml({ ...ranBrand, idea }, previewLabels) : null),
        [ranBrand, idea, previewLabels]
    );

    const canRun = appName.trim().length > 0;

    const handleRun = async () => {
        if (!canRun || isSaving) return;

        const brand = {
            appName: appName.trim(),
            tagline: tagline.trim(),
            accentColor,
            emoji,
        };

        setRanBrand(brand);
        setError(null);

        // The win is the preview, so it lands first and completion follows even
        // if persistence fails — a save error must never cost them the lesson.
        onComplete?.();

        setIsSaving(true);
        try {
            const token = await getFreshIdToken(user?.accessToken);
            if (token) await saveMyProject(brand, token);
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div ref={containerRef} className="prompt-lab-root canvas-layout-root">
            {/* LEFT: Lesson */}
            <div
                className="prompt-lab-pane prompt-lab-pane--lesson canvas-layout-lesson"
                style={{ width: leftWidth }}
            >
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">
                                {lessonTitle || definition?.title || t("promptLab.lessonFallback")}
                            </CardTitle>
                            <ListenButton text={definition?.lessonMarkdown} lessonID={definition?.lessonID} />
                        </CardHeader>
                        <CardBody className="prompt-lab-lesson-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {definition?.lessonMarkdown || ""}
                            </ReactMarkdown>

                            {definition?.exercisePrompt && (
                                <div className="prompt-lab-exercise">
                                    <strong>{t("promptLab.yourExercise")}</strong>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {definition.exercisePrompt}
                                    </ReactMarkdown>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Draggable border: Lesson ↔ form */}
            <PaneResizer
                value={leftWidth}
                {...leftLimits(containerWidth())}
                onResize={setLeftWidth}
                onPointerDown={startLeftResize}
                label={t("promptLab.resizePane", { pane: t("promptLab.lessonFallback") })}
                title={t("promptLab.dragToResize", "Drag to resize")}
            />

            {/* MIDDLE: the brand form — where the student works */}
            <div className="prompt-lab-pane template-studio-form" style={{ width: middleWidth, flex: "0 0 auto" }}>
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">{t("templateStudio.title")}</CardTitle>
                            {allPassed && (
                                <span className="canvas-layout-done">
                                    <FontAwesomeIcon icon={faCircleCheck} /> {t("promptLab.complete")}
                                </span>
                            )}
                        </CardHeader>
                        <CardBody className="template-studio-form-body">
                            <p className="template-studio-sub">{t("templateStudio.subtitle")}</p>

                            <label className="template-studio-label" htmlFor="ts-name">
                                {t("templateStudio.nameLabel")}
                            </label>
                            <input
                                id="ts-name"
                                className="template-studio-input"
                                type="text"
                                value={appName}
                                onChange={(e) => setAppName(e.target.value)}
                                placeholder={t("templateStudio.namePlaceholder")}
                                maxLength={40}
                            />

                            <label className="template-studio-label" htmlFor="ts-tagline">
                                {t("templateStudio.taglineLabel")}
                            </label>
                            <input
                                id="ts-tagline"
                                className="template-studio-input"
                                type="text"
                                value={tagline}
                                onChange={(e) => setTagline(e.target.value)}
                                placeholder={t("templateStudio.taglinePlaceholder")}
                                maxLength={70}
                            />

                            <span className="template-studio-label">{t("templateStudio.colorLabel")}</span>
                            <div className="template-studio-swatches">
                                {ACCENT_COLORS.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`template-studio-swatch${c === accentColor ? " is-selected" : ""}`}
                                        style={{ background: c }}
                                        onClick={() => setAccentColor(c)}
                                        aria-label={c}
                                        aria-pressed={c === accentColor}
                                    />
                                ))}
                            </div>

                            <span className="template-studio-label">{t("templateStudio.emojiLabel")}</span>
                            <div className="template-studio-emojis">
                                {EMOJI_CHOICES.map((e) => (
                                    <button
                                        key={e}
                                        type="button"
                                        className={`template-studio-emoji${e === emoji ? " is-selected" : ""}`}
                                        onClick={() => setEmoji(e)}
                                        aria-label={e}
                                        aria-pressed={e === emoji}
                                    >
                                        {e}
                                    </button>
                                ))}
                            </div>

                            <Button
                                type="button"
                                color="primary"
                                className="template-studio-run"
                                onClick={handleRun}
                                disabled={!canRun}
                            >
                                <FontAwesomeIcon icon={faPlay} />{" "}
                                {ranBrand ? t("templateStudio.update") : t("templateStudio.run")}
                            </Button>

                            {!canRun && (
                                <p className="template-studio-need-name">{t("templateStudio.needName")}</p>
                            )}
                            {error && <div className="template-studio-error">{error}</div>}
                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Draggable border: form ↔ preview */}
            <PaneResizer
                value={middleWidth}
                {...middleLimits(containerWidth())}
                onResize={setMiddleWidth}
                onPointerDown={startMiddleResize}
                label={t("promptLab.resizePane", { pane: t("templateStudio.title") })}
                title={t("promptLab.dragToResize", "Drag to resize")}
            />

            {/* RIGHT: the live preview — the point of the lesson */}
            <div className="prompt-lab-pane prompt-lab-pane--right template-studio-preview" style={{ flex: "1 1 auto" }}>
                <Card className="prompt-lab-card">
                    <CardHeader className="prompt-lab-card-header">
                        <CardTitle tag="h5" className="mb-0">{t("templateStudio.previewTitle")}</CardTitle>
                        <span className="template-studio-preview-sub">{t("templateStudio.previewSub")}</span>
                    </CardHeader>
                    <CardBody className="template-studio-preview-body">
                        {previewHtml ? (
                            <iframe
                                className="template-studio-frame"
                                title={t("templateStudio.previewTitle")}
                                srcDoc={previewHtml}
                                // The document is generated locally from the
                                // student's own input; sandboxing without
                                // allow-same-origin keeps it inert regardless.
                                sandbox="allow-scripts"
                            />
                        ) : (
                            <div className="template-studio-empty">{t("templateStudio.emptyPreview")}</div>
                        )}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default TemplateStudioLayout;
